import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Stack,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { confirmDialog } from "../components/ConfirmDialog";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function UserManagement() {
  const axiosPrivate = useAxiosPrivate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success, error, warning, info
  });
  const [formData, setFormData] = useState({
    user: "",
    email: "",
    pwd: "",
    roles: 1,
  });

  const roleOptions = [
    { value: 1, label: "User", color: "default" },
    { value: 2, label: "Editor", color: "info" },
    { value: 4, label: "Admin", color: "warning" },
    { value: 7, label: "Super Admin", color: "error" },
  ];

  const getRoleLabel = (roleValue) => {
    // Convert string to number for comparison
    const numValue =
      typeof roleValue === "string" ? parseInt(roleValue) : roleValue;
    const role = roleOptions.find((r) => r.value === numValue);
    return role ? role.label : "Unknown";
  };

  const getRoleColor = (roleValue) => {
    // Convert string to number for comparison
    const numValue =
      typeof roleValue === "string" ? parseInt(roleValue) : roleValue;
    const role = roleOptions.find((r) => r.value === numValue);
    return role ? role.color : "default";
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "Username", headerName: "Username", width: 200 },
    { field: "Email", headerName: "Email", width: 250 },
    {
      field: "Roles",
      headerName: "Role",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          color={getRoleColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteConfirm(params.row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch users: " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddNew = () => {
    setEditMode(false);
    setSelectedUser(null);
    setFormData({
      user: "",
      email: "",
      pwd: "",
      roles: 1,
    });
    setOpenDialog(true);
  };

  const handleEdit = (user) => {
    setEditMode(true);
    setSelectedUser(user);
    setFormData({
      user: user.Username,
      email: user.Email,
      pwd: "", // Don't pre-fill password
      roles: user.Roles,
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setFormData({
      user: "",
      email: "",
      pwd: "",
      roles: 1,
    });
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.user) {
      setSnackbar({
        open: true,
        message: "Username is required",
        severity: "warning",
      });
      return;
    }

    if (!editMode && !formData.pwd) {
      setSnackbar({
        open: true,
        message: "Password is required for new users",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      if (editMode) {
        // Update existing user
        const updateData = {
          user: formData.user,
          email: formData.email,
          roles: formData.roles,
        };
        // Only include password if it was changed
        if (formData.pwd) {
          updateData.pwd = formData.pwd;
        }

        await axiosPrivate.put(`/users/${selectedUser.id}`, updateData);
        setSnackbar({
          open: true,
          message: "User updated successfully!",
          severity: "success",
        });
      } else {
        // Create new user
        await axiosPrivate.post("/users", formData);
        setSnackbar({
          open: true,
          message: "User created successfully!",
          severity: "success",
        });
      }

      handleClose();
      fetchUsers();
    } catch (error) {
      console.error("Error saving user:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          `Failed to ${editMode ? "update" : "create"} user: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (user) => {
    confirmDialog(
      `Are you sure you want to delete user "${user.Username}"? This action cannot be undone.`,
      () => handleDelete(user.id),
      false,
    );
  };

  const handleDelete = async (userId) => {
    try {
      setLoading(true);
      await axiosPrivate.delete(`/users/${userId}`);
      setSnackbar({
        open: true,
        message: "User deleted successfully!",
        severity: "success",
      });
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          `Failed to delete user: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New User
        </Button>
      </Stack>

      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        sx={{ bgcolor: "background.paper", height: 600 }}
      />

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? "Edit User" : "Add New User"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                required
                fullWidth
                label="Username"
                name="user"
                value={formData.user}
                onChange={handleChange}
                autoFocus={!editMode}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
              <TextField
                required={!editMode}
                fullWidth
                label={
                  editMode
                    ? "New Password (leave blank to keep current)"
                    : "Password"
                }
                name="pwd"
                type={showPassword ? "text" : "password"}
                value={formData.pwd}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}

                        {/* Snackbar for notifications */}
                        <Snackbar
                          open={snackbar.open}
                          autoHideDuration={6000}
                          onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                          }
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                        >
                          <Alert
                            onClose={() =>
                              setSnackbar({ ...snackbar, open: false })
                            }
                            severity={snackbar.severity}
                            sx={{ width: "100%" }}
                          >
                            {snackbar.message}
                          </Alert>
                        </Snackbar>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="roles"
                  value={formData.roles}
                  onChange={handleChange}
                  label="Role"
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {editMode ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
